<template>
  <v-expansion-panels v-model="panel">
    <v-expansion-panel elevation="0" rounded="lg" v-for="log in logs">
      <v-expansion-panel-title height="48px" style="background-color: #7077bb4D">
        <span class="reg-normal">{{ log.week_start.format("D MMMM YYYY") }} - {{ log.week_end.format("D MMMM YYYY") }} ({{ log.messages.length }})</span>
        <template #actions="{ expanded }">
          <v-icon size="large" :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"/>
        </template>
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <v-list v-if="log.messages && log.messages.length > 0" density="compact">
          <template v-for="message in log.messages">
            <v-list-item color="primary">
              <v-list-item-title>
                <span class="sm-normal text-primary">{{ message.message }}</span>
              </v-list-item-title>
              <v-list-item-subtitle>
                <span class="tiny-normal">{{ message.creator + ' on ' + moment(message.timestamp, "DD-MM-YYYY HH:mm:ss").format("D MMMM YYYY HH:mm:ss") }}</span>
              </v-list-item-subtitle>
              <template v-if="canEdit && message.editable" #append>
                <v-list-item-action>
                  <v-menu>
                    <template #activator="{ props }">
                      <v-btn elevation="0" icon v-bind="props">
                        <PhDotsThreeVertical size="24" style="cursor: pointer;"/>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="editLog(message)">
                        <template #prepend><PhPencilSimpleLine size="24"/></template>
                        <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('edit') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="deleteLog(message)">
                        <template #prepend><PhTrash size="24"/></template>
                        <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('delete') }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-list-item-action>
              </template>
            </v-list-item>
            <v-divider/>
          </template>
        </v-list>
        <div class="reg-normal text-primary mt-3" v-else>
          {{ $store.getters.translate("nothing_found") }}
        </div>
        <v-text-field
            v-if="!isDisabled && log.week_end > moment.now()"
            density="default"
            class="reg-normal text-primary mt-5"
            base-color="border-tertiary"
            bg-color="secondary"
            color="primary"
            v-model="new_message"
            @keyup.enter="insertContent()"
            hide-details
            single-line
            flat
            clearable>
          <template #label>
            <span class="sm-normal text-subtle">{{ $store.getters.translate('add_to_log') }}</span>
          </template>
          <template #append>
            <base-action-button @click="insertContent()">
              <template #prepend><PhPlus style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
              {{ $store.getters.translate('add') }}
            </base-action-button>
          </template>
        </v-text-field>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
  <edit-log ref="edit_meeting" @edited="meetingEdited" />
</template>

<script>
import helpFunctions from "../../plugins/helpFunctions";
import moment from "moment";
import BaseActionButton from "./BaseActionButton";
import {PhDotsThreeVertical, PhPencilSimpleLine, PhPlus, PhTrash} from "@phosphor-icons/vue";
import {useAbility} from "@casl/vue";
import EditLog from "../../views/meetings/Edit";

export default {
  components: {EditLog, PhTrash, PhPencilSimpleLine, PhDotsThreeVertical, BaseActionButton, PhPlus},
  props: ["model_id", "isDisabled"],
  setup() {
    const { can } = new useAbility();
    return { can }
  },
  data() {
    return {
      page: helpFunctions.pages.meetings,
      logs: [],
      new_message: null,
      panel: [0]
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      if (this.model_id) {
        this.logs = [];
        let response = await helpFunctions.getCustomRoute(this.$toast, this.page.name + "?project_management_id=" + this.model_id);
        for (let i = 0; i < 100; i++) {
          let temp_logs = response.filter(item => moment(item.created_at) > moment().subtract(i, 'weeks').startOf('isoWeek') && moment(item.created_at) < moment().subtract(i, 'weeks').endOf('isoWeek'));
          if(temp_logs.length > 0 || i === 0) {
            let messages = [];
            for (let x = 0; x < temp_logs.length; x++) {
              let parts = temp_logs[x].log.split("] ", 2);
              messages.push({
                id: temp_logs[x].id,
                timestamp: parts[0].split(" - ")[0].replace("[", ""),
                creator: parts[0].split(" - ")[1],
                message: parts[1],
                editable: temp_logs[x].created_by === this.$store.getters.getUser.id,
                metadata: parts[0] + "] ",
              });
            }
            this.logs.push({
              week_start: moment().subtract(i, 'weeks').startOf('isoWeek'),
              week_end: moment().subtract(i, 'weeks').endOf('isoWeek'),
              messages: messages,
            });
          }
        }
      }
    },
    async insertContent() {
      if (this.new_message) {
        let timestamp = moment().format("DD-MM-YYYY HH:mm:ss");
        let metadata = "[" + timestamp + " - " + this.$store.getters.getUser.person.casual_name + "] ";

        let record_id = await helpFunctions.createModel(this.$toast, this.page.name, { project_management_id: this.model_id, log: metadata + this.new_message });

        this.logs[0].messages.unshift({
          id: record_id,
          timestamp: timestamp,
          creator: this.$store.getters.getUser.person.casual_name,
          message: this.new_message,
          editable: true,
          metadata: metadata,
        });

        this.new_message = null;
      }
    },
    editLog(message) {
      this.$refs.edit_meeting.load(message);
    },
    async meetingEdited(message) {
      await helpFunctions.saveModel(this.$toast, this.page.name, { id: message.id, log: message.metadata + message.message });
    },
    async deleteLog(message) {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async (res) => {
        if (res) {
          await helpFunctions.deleteModel(this.$toast, this.page.name, message.id);
          await this.load();
        }
      });
    },
  },
  computed: {
    canEdit() {
      return this.can("edit", this.$createEntity("log", { id: 0 }));
    },
  },
  watch: {
    model_id: {
      handler() {
        this.load();
      },
    },
  },
};
</script>